import { useMemo, useState } from 'react';

import type { ConfigFileOverride, ConfigurationFile, EnvironmentSummary } from '@/api/configuration';
import { resolveConfigFields } from '@/components/deploy/resolve-config-fields';
import { useConfigFileOverrides } from '@/hooks/use-config-overrides';

// --- Props ---

interface ConfigFileListItemProps {
  projectKey: string;
  configFile: ConfigurationFile;
  environments: EnvironmentSummary[];
  onEdit?: (configFile: ConfigurationFile) => void;
  onDelete?: (configFile: ConfigurationFile) => void;
  onManageOverrides?: (configFile: ConfigurationFile) => void;
}

// --- Helpers ---

function OverrideCountBadge({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <span className="inline-flex items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900/30 px-1.5 py-0.5 text-[10px] font-semibold text-brand-700 dark:text-brand-300 min-w-[18px]">
      {count}
    </span>
  );
}

function OverrideDetailRow({
  override,
  configFile,
  environmentName,
}: {
  override: ConfigFileOverride;
  configFile: ConfigurationFile;
  environmentName: string;
}) {
  const resolved = useMemo(
    () => resolveConfigFields(configFile, override),
    [configFile, override]
  );

  // Collect fields that differ from the defaults (i.e. have an override value)
  const overriddenFields: { label: string; value: string }[] = [];
  if (resolved.sourceRoot.source === 'override') {
    overriddenFields.push({ label: 'Source Root', value: resolved.sourceRoot.value });
  }
  if (resolved.targetLocation.source === 'override') {
    overriddenFields.push({ label: 'Target Location', value: resolved.targetLocation.value });
  }
  if (resolved.targetFilename.source === 'override') {
    overriddenFields.push({ label: 'Target Filename', value: resolved.targetFilename.value });
  }

  if (overriddenFields.length === 0) return null;

  return (
    <div className="flex items-start gap-2 text-xs">
      <span className="flex-shrink-0 font-medium text-content-secondary w-24 truncate" title={environmentName}>
        {environmentName}
      </span>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-content-tertiary">
        {overriddenFields.map((field) => (
          <span key={field.label}>
            <span className="text-content-secondary">{field.label}:</span>{' '}
            <span className="text-content">{field.value}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// --- Component ---

export function ConfigFileListItem({
  projectKey,
  configFile,
  environments,
  onEdit,
  onDelete,
  onManageOverrides,
}: ConfigFileListItemProps) {
  const [expanded, setExpanded] = useState(false);

  const {
    data: overrides = [],
    isLoading: overridesLoading,
    isError: overridesError,
  } = useConfigFileOverrides(projectKey, configFile.id);

  const overrideCount = overrides.length;

  // Map environment IDs to names for display
  const envNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const env of environments) {
      map.set(env.id, env.name);
    }
    return map;
  }, [environments]);

  const handleToggleExpand = () => {
    setExpanded((prev) => !prev);
  };

  return (
    <li className="rounded-lg border border-border p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {/* File name row */}
          <div className="flex items-center gap-2">
            {/* Expand/collapse toggle — only shown when overrides exist */}
            {overrideCount > 0 && (
              <button
                type="button"
                onClick={handleToggleExpand}
                aria-expanded={expanded}
                aria-label={expanded ? 'Collapse override details' : 'Expand override details'}
                className="flex-shrink-0 rounded p-0.5 text-content-tertiary hover:text-content-secondary hover:bg-surface-raised transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1"
              >
                <svg
                  className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            <p className="text-sm font-medium text-content">{configFile.name}</p>

            {/* Override count badge */}
            {!overridesLoading && <OverrideCountBadge count={overrideCount} />}
          </div>

          {/* Metadata row — Default Source Root */}
          <div className="mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-content-secondary">
            <span>
              Source Root:{' '}
              {configFile.defaultSourceRoot ? (
                <span className="text-content">{configFile.defaultSourceRoot}</span>
              ) : (
                <span className="italic text-content-tertiary">not set</span>
              )}
            </span>
            {configFile.type && <span>Type: {configFile.type}</span>}
            {configFile.targetLocation && <span>Target: {configFile.targetLocation}</span>}
            {configFile.targetFilename && <span>Tgt file: {configFile.targetFilename}</span>}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-shrink-0 items-center gap-1">
          {onEdit && (
            <button
              type="button"
              onClick={() => onEdit(configFile)}
              className="rounded px-2 py-1 text-xs font-medium text-content-secondary hover:bg-surface-raised transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1"
            >
              Edit
            </button>
          )}
          {onManageOverrides && (
            <button
              type="button"
              onClick={() => onManageOverrides(configFile)}
              className="rounded px-2 py-1 text-xs font-medium text-content-secondary hover:bg-surface-raised transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1"
            >
              Overrides
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(configFile)}
              className="rounded px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Override data load error */}
      {overridesError && (
        <div className="mt-2 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2">
          <p className="text-xs text-red-700 dark:text-red-300">
            Override information is unavailable.
          </p>
        </div>
      )}

      {/* Expanded override details */}
      {expanded && !overridesError && overrideCount > 0 && (
        <div className="mt-2 rounded-md border border-border bg-surface p-2.5 space-y-1.5">
          <p className="text-[10px] font-medium text-content-tertiary uppercase tracking-wide mb-1">
            Environment Overrides
          </p>
          {overrides.map((override) => (
            <OverrideDetailRow
              key={override.id}
              override={override}
              configFile={configFile}
              environmentName={envNameMap.get(override.environmentId) ?? override.environmentId}
            />
          ))}
        </div>
      )}
    </li>
  );
}
